import { useCallback } from 'react';
import * as Cesium from 'cesium';
import type { LayerState } from '../../../types/mdai';

export function useVisualizationControls(
  viewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  secondaryViewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  layersRef: React.MutableRefObject<LayerState>,
  satActiveMap: React.MutableRefObject<Record<string, boolean>>,
  gsActiveMap: React.MutableRefObject<Record<string, boolean>>,
  geoFeatureActiveMap: React.MutableRefObject<Record<string, boolean>>,
  sharedDataSourceRef: React.MutableRefObject<any>,
  secondarySharedDataSourceRef: React.MutableRefObject<any>,
) {
  const updateLayers = useCallback((layers: LayerState) => {
    layersRef.current = layers;
    viewerRef.current?.scene.requestRender();
    secondaryViewerRef.current?.scene.requestRender();
  }, []);

  const setSatActive = useCallback((name: string, active: boolean) => {
    satActiveMap.current[name] = active;
    viewerRef.current?.scene.requestRender();
    secondaryViewerRef.current?.scene.requestRender();
  }, []);

  const setGSActive = useCallback((id: string, active: boolean) => {
    gsActiveMap.current[id] = active;
    viewerRef.current?.scene.requestRender();
    secondaryViewerRef.current?.scene.requestRender();
  }, []);

  const setGeoFeatureVisible = useCallback((featureId: string, visible: boolean) => {
    geoFeatureActiveMap.current[featureId] = visible;

    [sharedDataSourceRef, secondarySharedDataSourceRef].forEach((ref) => {
      const ds = ref?.current;
      if (ds) {
        const entity = ds.entities.getById(featureId);
        if (entity) {
          entity.show = visible;
        }
        const outlineEntity = ds.entities.getById(`${featureId}_outline`);
        if (outlineEntity) {
          outlineEntity.show = visible;
        }
      }
    });

    viewerRef.current?.scene.requestRender();
    secondaryViewerRef.current?.scene.requestRender();
  }, [sharedDataSourceRef, secondarySharedDataSourceRef]);

  const setSceneMode = useCallback((mode: 'SCENE3D' | 'SCENE2D') => {
    const v = viewerRef.current;
    if (!v) return;
    if (mode === 'SCENE3D') {
      v.scene.morphTo3D(0.0);
    } else {
      v.scene.morphTo2D(0.0);
    }
  }, []);

  const toggleLighting = useCallback(() => {
    const v = viewerRef.current;
    if (!v) return;
    v.scene.globe.enableLighting = !v.scene.globe.enableLighting;
  }, []);

  return {
    updateLayers,
    setSatActive,
    setGSActive,
    setGeoFeatureVisible,
    setSceneMode,
    toggleLighting,
  };
}
